import React, {useRef, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {useSelector} from "react-redux";
import PageHeader from "../../components/page-header";
import Icon from "../../../system/components/icon";
import {useTranslate} from "../../../system/useTranslate";
import {useWorkspace} from "../../../models/workspace/use-workspace";
import {normalizeApiError} from "../../../system/api";

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
}

export function base64BackupToBlob(value) {
    const binary = atob(value || "");
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], {type: "application/vnd.zofino.workspace-backup"});
}

function WorkspacePortability() {
    const translate = useTranslate();
    const workspaceApi = useWorkspace();
    const workspaceData = useSelector((state) => state.app.workspaceMembershipData?.workspace);
    const fileRef = useRef();
    const [file, setFile] = useState(null);
    const [confirmation, setConfirmation] = useState("");
    const [dialogVisible, setDialogVisible] = useState(false);
    const [loading, setLoading] = useState("");
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const workspaceId = workspaceData?.documentId;
    const interpolate = (key, values) => Object.entries(values || {}).reduce(
        (text, [name, value]) => text.replaceAll(`{{${name}}}`, String(value ?? "")),
        translate(key),
    );

    const exportWorkspace = async () => {
        setLoading("export");
        setError("");
        try {
            const response = await workspaceApi.exportWorkspace(workspaceId);
            const disposition = response?.headers?.["content-disposition"] || "";
            const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || "zofino-workspace.zofbackup";
            downloadBlob(response.data, filename);
        } catch (requestError) {
            setError(translate(normalizeApiError(requestError).message || "workspace-portability.error.export"));
        } finally {
            setLoading("");
        }
    };

    const chooseImport = (event) => {
        const selected = event.target.files?.[0];
        event.target.value = "";
        if (!selected) return;
        setFile(selected);
        setConfirmation("");
        setDialogVisible(true);
    };

    const importWorkspace = async () => {
        setLoading("import");
        setError("");
        try {
            const data = await workspaceApi.importWorkspace(workspaceId, file);
            if (data?.automaticBackup?.contentBase64) {
                downloadBlob(base64BackupToBlob(data.automaticBackup.contentBase64), data.automaticBackup.filename);
            }
            setResult(data);
            setDialogVisible(false);
            setFile(null);
        } catch (requestError) {
            setError(translate(normalizeApiError(requestError).message || "workspace-portability.error.import"));
        } finally {
            setLoading("");
        }
    };

    return (
        <div className="h-full overflow-auto p-4 flex flex-column gap-4">
            <PageHeader title="workspace-portability" description="workspace-portability-description" titleClassName="text-lg" descriptionClassName="text-xs"/>
            {error && <Message severity="error" text={error}/>} 
            {result && <Message severity="success" text={interpolate("workspace-portability.import-success", {
                CREATED: result.created || 0,
                UPDATED: result.updated || 0,
                SKIPPED: result.skipped || 0,
            })}/>} 
            <div className="grid">
                <section className="col-12 lg:col-6">
                    <article className="h-full surface-card border-1 border-200 border-round-xl shadow-1 p-4 flex flex-column gap-3">
                        <span className="w-3rem h-3rem border-round-lg bg-orange-50 flex align-items-center justify-content-center"><Icon icon="mi mi:file_export" className="text-2xl text-primary"/></span>
                        <strong className="text-base">{translate("workspace-export")}</strong>
                        <p className="text-xs text-500 line-height-3 m-0 flex-1">{translate("workspace-export-description")}</p>
                        <Button className="text-xs gap-2 w-fit" icon="pi pi-download" loading={loading === "export"} label={translate("download-workspace-backup")} onClick={exportWorkspace}/>
                    </article>
                </section>
                <section className="col-12 lg:col-6">
                    <article className="h-full surface-card border-1 border-200 border-round-xl shadow-1 p-4 flex flex-column gap-3">
                        <span className="w-3rem h-3rem border-round-lg bg-cyan-50 flex align-items-center justify-content-center"><Icon icon="pi pi-file-import" className="text-2xl text-cyan-600"/></span>
                        <strong className="text-base">{translate("workspace-import")}</strong>
                        <p className="text-xs text-500 line-height-3 m-0 flex-1">{translate("workspace-import-description")}</p>
                        <input ref={fileRef} type="file" hidden accept=".zofbackup,application/vnd.zofino.workspace-backup" onChange={chooseImport}/>
                        <Button outlined className="text-xs gap-2 w-fit" icon="pi pi-upload" label={translate("choose-workspace-backup")} onClick={() => fileRef.current?.click()}/>
                    </article>
                </section>
            </div>
            <Dialog visible={dialogVisible} modal className="w-11 md:w-6 lg:w-4 rtl" header={translate("workspace-import-confirm-title")} onHide={() => !loading && setDialogVisible(false)}
                    footer={<div className="flex gap-2 justify-content-end">
                        <Button text label={translate("cancel")} disabled={loading === "import"} onClick={() => setDialogVisible(false)}/>
                        <Button severity="danger" loading={loading === "import"} disabled={confirmation !== workspaceData?.name}
                                label={translate("confirm-workspace-import")} onClick={importWorkspace}/>
                    </div>}>
                <div className="flex flex-column gap-3">
                    <Message severity="warn" text={translate("workspace-import-backup-warning")}/>
                    <span className="text-xs text-600">{interpolate("workspace-import-confirm-description", {NAME: workspaceData?.name || ""})}</span>
                    <InputText value={confirmation} className="w-full text-right" placeholder={workspaceData?.name} onChange={(event) => setConfirmation(event.target.value)}/>
                    <small className="text-xs text-500 ltr text-left">{file?.name}</small>
                </div>
            </Dialog>
        </div>
    );
}

export default WorkspacePortability;
